import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDarkMode } from "../../components/context/darkModeContext";
import { categorySalesService } from "../../utils/services/categorySalesService";
import { categoryForecastService } from "../../utils/services/categoryForecastService";
import { catalogPlanService } from "../../utils/services/catalogPlanService";
import { catalogPlanManagementService } from "../../utils/services/catalogPlanManagementService";
import { planForecastRunService } from "../../utils/services/planForecastRunService";
import { toast } from "react-toastify";
import BudgetSummary from "./components/BudgetSummary";
import PrintPanel from "./components/PrintPanel";
import PlannerTable from "./components/PlannerTable";
import RunForecastDialog, { fmtDuration } from "./components/RunForecastDialog";
import PlannerSkeleton from "./components/PlannerSkeleton";
import { fmtPct } from "./utils";
import { normalizeRowMonthly } from "./planMonthly";

// ─── Print run constants ───────────────────────────────────────────────────────
// 1 copy generates $5 in revenue on average
const SPC = 5.0;
// Seasonal distribution of print copies (sums to 1.0)
const PRINT_SEASONAL = [
  0.06, 0.06, 0.07, 0.08, 0.08, 0.08, 0.07, 0.08, 0.09, 0.1, 0.11, 0.12,
];

/** Derive initial printM (monthly K-copy counts) from a budget in raw $ */
function budgetToPrintM(budgetRaw) {
  const totalK = Math.round(budgetRaw / 1000 / SPC);
  const m = PRINT_SEASONAL.map((s) => Math.round(totalK * s));
  const diff = totalK - m.reduce((s, v) => s + v, 0);
  m[11] += diff;
  return m;
}

const CURRENT_YEAR = new Date().getFullYear();

// Channel filter status from a plan GET: channels present = ready (also for
// a backend that doesn't send channel_status yet).
const channelStatusOf = (plan) =>
  plan?.channel_view?.length ? "ready" : plan?.channel_status || "preparing";

// A saved allocation is keyed by category_id (category plans) or product_type
// (product-type plans); the planner's row `_id` is the matching value.
const savedRowKey = (c) => c?.product_type ?? c?.category_id;

// ─── Main Page ────────────────────────────────────────────────────────────────
// Next-year plans: LY includes a rest-of-year forecast from this month on.
const LY_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const lyForecastRangeOf = (categories) => {
  const from = (categories || []).find((c) => c.ly_forecast_from_month)?.ly_forecast_from_month;
  if (!from) return "";
  return from === 12 ? "Dec" : `${LY_MONTHS[from - 1]}–Dec`;
};

export default function CatalogPlanner() {
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const { id: planId } = useParams(); // present when editing a saved plan
  const location = useLocation();
  // client_id: prefer localStorage (client portal), fall back to state passed from New Plan modal (admin)
  const clientId =
    localStorage.getItem("client_id") || location.state?.client_id || null;
  const currentYear = new Date().getFullYear();

  // True from the first paint until the plan and its sales data are in.
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]); // root categories with children
  const [currentYearData, setCurrentYearData] = useState(currentYear);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);

  // plan values keyed by category _id (in raw $, not K)
  const [planValues, setPlanValues] = useState({});
  // Monthly shape per leaf _id (12 raw $) — the planner's month edits, or the
  // saved split. The months shown are derived from it (see planMonthlyById).
  const [planShapes, setPlanShapes] = useState({});
  // Closed (ended) months of the plan year: their plan is the actual.
  const [closedMonth, setClosedMonth] = useState(0);
  // Per-row values the server works out on GET (avg_unit_price, …), keyed by _id
  const [savedRowMeta, setSavedRowMeta] = useState({});
  // Channel filter: the server's plan per sales channel (GET channel_view,
  // by each channel's sales ratio) and the channel being viewed ("ALL" =
  // the whole plan, editable).
  const [channelView, setChannelView] = useState(null);
  // "unsaved" (new plan, not saved yet) | "preparing" (channel allocation
  // being worked out on the server) | "ready" | "none" (no channel sales).
  const [channelStatus, setChannelStatus] = useState(() => (planId ? "preparing" : "unsaved"));
  const [activeChannel, setActiveChannel] = useState("ALL");
  const channelRefreshTimer = useRef(null);
  // locked state keyed by _id
  const [locked, setLocked] = useState({});
  // expanded state keyed by parent _id
  const [expanded, setExpanded] = useState({});
  // total budget cap (editable)
  const [totalBudget, setTotalBudget] = useState(0);
  // active filter (root category name or "ALL")
  const [activeFilter, setActiveFilter] = useState("ALL");
  // scenario
  const [scenario, setScenario] = useState("start-of-year");
  // planning dimension: "category" | "product_type" (fixed at creation)
  const [dimension, setDimension] = useState(
    location.state?.dimension === "product_type" ? "product_type" : "category",
  );
  const isProductType = dimension === "product_type";

  // existing plan meta when editing
  const [planMeta, setPlanMeta] = useState(null);

  // ── Plan meta for saving ────────────────────────────────────────────────
  const [modalName, setModalName] = useState("");
  const [modalYear, setModalYear] = useState(currentYear);
  const [modalStatus, setModalStatus] = useState("draft");

  // ── Category statuses (plan-category level) ──────────────────────────────
  const [categoryStatuses, setCategoryStatuses] = useState({}); // category_id -> 'draft'|'review'|'approved'

  // ── Print run state (frontend-only, derived from budget) ──────────────────
  const [printM, setPrintM] = useState([]); // 12 monthly K-copy counts

  // ── Forecast async polling ────────────────────────────────────────────────
  const forecastPollRef = useRef(null);
  const [forecastErrors, setForecastErrors] = useState({}); // category_id -> error string
  const [forecastPending, setForecastPending] = useState(false); // true while background job is running

  // ── Plan forecast runs (Run Forecast / View Forecast per category) ───────
  const [forecastRuns, setForecastRuns] = useState({}); // row_key → latest run
  const [runDialog, setRunDialog] = useState(null); // { rowKey, name, skuHint }
  const [cancelTarget, setCancelTarget] = useState(null); // run awaiting cancel confirmation
  const [cancelling, setCancelling] = useState(false);
  const runsRef = useRef({});

  // ── Auto-save ───────────────────────────────────────────────────────────
  const autoSavedIdRef = useRef(null); // ID of auto-created plan (new plans only)
  const saveTimerRef = useRef(null); // debounce timer (5 s)
  const skipNextInitRef = useRef(false); // skip init re-run after auto-save URL update
  const buildPayloadRef = useRef(null); // always-current payload snapshot
  const triggerAutoSaveRef = useRef(null); // always-current triggerAutoSave (used inside polling closure)
  const scenarioRef = useRef("start-of-year"); // previous scenario, to detect real switches
  const baseBudgetRef = useRef(0); // budget target to restore on non-"match-ly" scenarios
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // 'idle'|'saving'|'saved'

  // Debounced auto-save: fires 5 s after last user change
  const triggerAutoSave = useCallback(() => {
    if (saving) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      saveTimerRef.current = null;
      const payload = buildPayloadRef.current?.();
      if (!payload?.name?.trim()) return;
      setAutoSaveStatus("saving");
      try {
        const effectivePlanId = planId || autoSavedIdRef.current;
        if (effectivePlanId) {
          delete payload.client_id; // client_id is immutable after creation
          await catalogPlanService.update(effectivePlanId, payload);
        } else {
          const saved = await catalogPlanService.create(payload);
          const newId = saved._id?.toString();
          autoSavedIdRef.current = newId;
          skipNextInitRef.current = true;
          navigate(`/catalog-planner/${newId}`, { replace: true });
        }
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus("idle"), 2500);
      } catch (err) {
        console.error("Auto-save error:", err);
        setAutoSaveStatus("idle");
      }
    }, 5000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, navigate, saving]);

  // Cancel pending auto-save on unmount
  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  const textPrimary = darkMode ? "#fff" : "#111";
  const textSecondary = darkMode ? "#aaa" : "#666";
  const borderColor = darkMode ? "#333" : "#e8e8e8";

  // ── Stop polling helper ───────────────────────────────────────────────────
  const stopForecastPolling = useCallback(() => {
    if (forecastPollRef.current) {
      clearInterval(forecastPollRef.current);
      forecastPollRef.current = null;
    }
    setForecastPending(false);
  }, []);

  // ── Start polling for a batch ─────────────────────────────────────────────
  const startForecastPolling = useCallback(
    (batchId) => {
      stopForecastPolling();
      setForecastPending(true);
      forecastPollRef.current = setInterval(async () => {
        try {
          const result = await categoryForecastService.poll(batchId);
          if (!result?.items?.length) return;

          // Build a lookup: categoryId -> item (only resolved items)
          const resolvedMap = {};
          result.items.forEach((it) => {
            if (it.status !== "pending" && it.forecast_monthly) {
              resolvedMap[it.category_id] = it;
            }
          });

          if (Object.keys(resolvedMap).length > 0) {
            setCategories((prev) =>
              prev.map((parent) => {
                const parentId = String(parent._id);

                // Update each child's forecast_monthly from the resolved map
                let anyChildUpdated = false;
                const updatedChildren = (parent.children || []).map((child) => {
                  const childId = String(child._id);
                  const item = resolvedMap[childId];
                  if (!item) return child;
                  anyChildUpdated = true;
                  return { ...child, forecast_monthly: item.forecast_monthly };
                });

                const parentItem = resolvedMap[parentId];

                if (parentItem) {
                  // Parent's own forecast arrived – re-aggregate cleanly:
                  // parent's OWN API forecast + each child's current forecast
                  const aggFm = Array.from({ length: 12 }, (_, i) => {
                    const sources = [
                      parentItem.forecast_monthly,
                      ...updatedChildren.map((c) => c.forecast_monthly),
                    ];
                    const vals = sources
                      .map((fm) => fm?.[i])
                      .filter((v) => v != null);
                    return vals.length === 0
                      ? null
                      : vals.reduce((s, v) => s + v, 0);
                  });
                  return {
                    ...parent,
                    forecast_monthly: aggFm,
                    children: updatedChildren,
                  };
                }

                // Parent still pending – update children, keep existing parent aggregate
                if (anyChildUpdated) {
                  return { ...parent, children: updatedChildren };
                }

                return parent;
              }),
            );
          }

          // Collect errors for display
          const errors = {};
          result.items.forEach((it) => {
            if (it.status === "error" && it.error)
              errors[it.category_id] = it.error;
          });
          if (Object.keys(errors).length) {
            setForecastErrors((prev) => ({ ...prev, ...errors }));
          }

          if (result.complete) {
            stopForecastPolling();
            // Save the freshly-arrived forecast data automatically
            triggerAutoSaveRef.current?.();
          }
        } catch (err) {
          console.warn("Forecast poll error:", err);
        }
      }, 4000); // poll every 4 seconds
    },
    [stopForecastPolling],
  );

  // Cleanup polling on unmount
  useEffect(() => () => stopForecastPolling(), [stopForecastPolling]);

  // ── Fetch category-sales data ─────────────────────────────────────────────
  const fetchSalesData = useCallback(
    async (year, { skipPolling = false, dimension: dimOverride } = {}) => {
      setLoading(true);
      try {
        const res = await categorySalesService.fetchPlanner(
          clientId,
          year,
          dimOverride ?? dimension,
        );
        const data = res?.data || [];
        setCategories(data);
        setCurrentYearData(res?.current_year || currentYear);
        // 0 is valid: a future plan year has no actual months yet
        setCurrentMonth(res?.current_month ?? new Date().getMonth() + 1);
        setClosedMonth(res?.closed_month ?? 0);
        // Start polling for async forecast results (skip when loading a saved plan)
        if (!skipPolling && res?.batch_id) startForecastPolling(res.batch_id);
        return data;
      } catch (err) {
        console.error("CatalogPlanner fetch failed:", err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [clientId, currentYear, dimension, startForecastPolling],
  );

  // ── Restore saved forecast values into live category data ─────────────────
  const applyStoredForecasts = (liveData, savedCategories) => {
    if (!savedCategories?.length) return liveData;
    const fmMap = {};
    savedCategories.forEach((c) => {
      // Skip forecasts saved before any were available (all null), so the live
      // forecast shows instead of an empty line.
      if (c.forecast_monthly?.some((v) => v != null))
        fmMap[savedRowKey(c)] = c.forecast_monthly;
    });
    if (!Object.keys(fmMap).length) return liveData;
    return liveData.map((parent) => {
      const parentId = parent._id.toString();
      if (parent.children?.length) {
        const updatedChildren = parent.children.map((child) => {
          const fm = fmMap[child._id.toString()];
          return fm ? { ...child, forecast_monthly: fm } : child;
        });
        // Re-aggregate parent forecast from children
        const aggFm = Array.from({ length: 12 }, (_, i) => {
          const vals = updatedChildren
            .map((c) => c.forecast_monthly?.[i])
            .filter((v) => v != null);
          return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
        });
        return {
          ...parent,
          forecast_monthly: aggFm,
          children: updatedChildren,
        };
      }
      const fm = fmMap[parentId];
      return fm ? { ...parent, forecast_monthly: fm } : parent;
    });
  };

  const initFromSalesData = useCallback(
    (data, savedCategories, savedTotalBudget, initialBudget = null) => {
      const isEdit = savedCategories != null && savedTotalBudget != null;
      const initPlans = {};
      const initExpanded = {};
      if (isEdit) {
        data.forEach((parent) => {
          const savedParent = savedCategories?.find(
            (c) => savedRowKey(c) === parent._id.toString(),
          );
          initPlans[parent._id.toString()] = savedParent?.plan_value ?? 0;
          initExpanded[parent._id.toString()] = true;
          (parent.children || []).forEach((child) => {
            const savedChild = savedCategories?.find(
              (c) => savedRowKey(c) === child._id.toString(),
            );
            initPlans[child._id.toString()] = savedChild?.plan_value ?? 0;
          });
        });
        baseBudgetRef.current = savedTotalBudget;
        setTotalBudget(savedTotalBudget);
        setPrintM(budgetToPrintM(savedTotalBudget));
      } else if (initialBudget != null) {
        // New plan with user-specified budget — distribute proportionally by LY
        const allLeafs = [];
        data.forEach((parent) => {
          initExpanded[parent._id.toString()] = true;
          if (parent.children?.length) {
            parent.children.forEach((child) =>
              allLeafs.push({
                id: child._id.toString(),
                lyRev: child.ly_revenue || 0,
              }),
            );
          } else {
            allLeafs.push({
              id: parent._id.toString(),
              lyRev: parent.ly_revenue || 0,
            });
          }
        });
        const lySum = allLeafs.reduce((s, c) => s + c.lyRev, 0);
        allLeafs.forEach((c) => {
          initPlans[c.id] =
            lySum > 0
              ? (c.lyRev / lySum) * initialBudget
              : initialBudget / Math.max(1, allLeafs.length);
        });
        baseBudgetRef.current = initialBudget;
        setTotalBudget(initialBudget);
        setPrintM(budgetToPrintM(initialBudget));
      } else {
        // New plan — default to LY values
        data.forEach((parent) => {
          initPlans[parent._id.toString()] = parent.ly_revenue ?? 0;
          initExpanded[parent._id.toString()] = true;
          (parent.children || []).forEach((child) => {
            initPlans[child._id.toString()] = child.ly_revenue ?? 0;
          });
        });
        const totalLY = data.reduce((s, p) => s + (p.ly_revenue || 0), 0);
        baseBudgetRef.current = totalLY || 0;
        setTotalBudget(totalLY || 0);
        setPrintM(budgetToPrintM(totalLY || 0));
      }
      setPlanValues(initPlans);
      setExpanded(initExpanded);
    },
    [],
  );

  useEffect(() => {
    if (skipNextInitRef.current) {
      skipNextInitRef.current = false;
      return;
    }
    const init = async () => {
      if (planId) {
        try {
          const plan = await catalogPlanService.getById(planId);
          setPlanMeta(plan);
          setModalName(plan.name || "");
          setModalYear(plan.year || currentYear);
          setModalStatus(plan.status || "draft");
          setScenario(plan.scenario || "start-of-year");
          const planDimension =
            plan.dimension === "product_type" ? "product_type" : "category";
          setDimension(planDimension);
          // Skip polling — use forecasts stored with the plan
          const data = await fetchSalesData(plan.year, {
            skipPolling: true,
            dimension: planDimension,
          });
          // Restore saved forecast values directly into the live category data
          const dataWithForecast = applyStoredForecasts(
            data,
            plan.categories || [],
          );
          if (dataWithForecast !== data) setCategories(dataWithForecast);
          initFromSalesData(
            dataWithForecast,
            plan.categories || [],
            plan.total_budget || 0,
          );
          const lockedMap = {};
          const statusMap = {};
          const shapes = {};
          const meta = {};
          (plan.categories || []).forEach((c) => {
            const key = savedRowKey(c);
            if (c.locked) lockedMap[key] = true;
            statusMap[key] = c.status || "active";
            if (c.plan_monthly?.length === 12) shapes[key] = c.plan_monthly;
            meta[key] = { avg_unit_price: c.avg_unit_price ?? null };
          });
          setPlanShapes(shapes);
          setSavedRowMeta(meta);
          setChannelView(plan.channel_view || null);
          setChannelStatus(channelStatusOf(plan));
          setLocked(lockedMap);
          setCategoryStatuses(statusMap);
        } catch (err) {
          console.error("Load plan error:", err);
          setLoading(false); // don't leave the skeleton up
        }
      } else {
        // Pre-fill modal from New Plan modal state (passed via router state)
        const locState = location.state || {};
        const initYear = locState.year || currentYear;
        if (locState.name) setModalName(locState.name);
        if (locState.year) setModalYear(locState.year);
        if (locState.status) setModalStatus(locState.status);
        if (locState.scenario) setScenario(locState.scenario);
        const data = await fetchSalesData(initYear);
        initFromSalesData(data, null, null, locState.budget || null);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  // Scenario switch → recompute budget + allocation.
  //  match-ly            : budget = LY total, allocation = LY exactly
  //  start/mid/end-of-year: keep the current budget, re-spread by LY seasonality
  useEffect(() => {
    if (!categories.length) return;
    const changed = scenarioRef.current !== scenario;
    scenarioRef.current = scenario;

    if (scenario === "match-ly") {
      const plans = {};
      let ly = 0;
      categories.forEach((parent) => {
        const leaves = parent.children?.length ? parent.children : [parent];
        leaves.forEach((c) => {
          const v = c.ly_revenue || 0;
          plans[c._id.toString()] = v;
          ly += v;
        });
      });
      setPlanValues(plans);
      setTotalBudget(ly);
      setPrintM(budgetToPrintM(ly));
      if (changed) triggerAutoSave();
    } else if (changed) {
      // Restore the plan's target budget and re-spread it by LY seasonality.
      const budget = baseBudgetRef.current || totalBudget;
      setTotalBudget(budget);
      setPrintM(budgetToPrintM(budget));
      distributeBudget(budget);
      triggerAutoSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, categories]);

  // ── Derived values ───────────────────────────────────────────────────────
  const allocated = categories.reduce((s, p) => {
    const childrenSum = (p.children || []).reduce(
      (cs, c) => cs + (planValues[c._id.toString()] || 0),
      0,
    );
    // If parent has children use children sum, else use parent plan directly
    return (
      s + (p.children?.length ? childrenSum : planValues[p._id.toString()] || 0)
    );
  }, 0);

  const variance = totalBudget - allocated;

  const totalLY = categories.reduce((s, p) => s + (p.ly_revenue || 0), 0);
  const planVsLYPct =
    totalLY > 0
      ? Math.round(((allocated - totalLY) / totalLY) * 1000) / 10
      : null;
  const planVsDelta = allocated - totalLY;


  const allLockedCount = Object.values(locked).filter(Boolean).length;

  const totalSubcategories = categories.reduce(
    (s, p) => s + (p.subcategory_count || 0),
    0,
  );

  // ── Dashboard derived values ─────────────────────────────────────────────
  const totalYTDActual = categories.reduce(
    (s, p) => s + (p.ytd_revenue || 0),
    0,
  );
  // Approximate YTD plan: pro-rate annual plan by current month
  const ytdPlanApprox = currentMonth > 0 ? allocated * (currentMonth / 12) : 0;

  // Leaf categories for approval counts
  const leafCategoryIds = categories.flatMap((p) =>
    p.children?.length
      ? p.children.map((c) => c._id.toString())
      : [p._id.toString()],
  );
  const approvalCounts = { draft: 0, review: 0, approved: 0 };
  leafCategoryIds.forEach((id) => {
    const s = categoryStatuses[id];
    const key =
      s === "review"
        ? "review"
        : s === "approved" || s === "active"
          ? "approved"
          : "draft";
    approvalCounts[key]++;
  });

  // Locked allocated sum for alert detection
  const lockedAllocated = leafCategoryIds
    .filter((id) => locked[id])
    .reduce((s, id) => s + (planValues[id] || 0), 0);

  // ── Filter tabs ──────────────────────────────────────────────────────────
  const filterTabs = ["ALL", ...categories.map((c) => c.name.toUpperCase())];


  // ── Toggle helpers ───────────────────────────────────────────────────────
  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const expandAll = () => {
    const all = {};
    categories.forEach((p) => (all[p._id.toString()] = true));
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});

  const handlePlanChange = (id, rawVal) => {
    const num = parseFloat(rawVal);
    if (isNaN(num) || num < 0) return;
    const editedValue = num * 1000;

    // Leaf categories hold the plan values (children, or childless parents).
    const allLeaves = [];
    categories.forEach((parent) => {
      if (parent.children?.length) {
        parent.children.forEach((child) =>
          allLeaves.push({
            id: child._id.toString(),
            lyRev: child.ly_revenue || 0,
          }),
        );
      } else {
        allLeaves.push({
          id: parent._id.toString(),
          lyRev: parent.ly_revenue || 0,
        });
      }
    });

    // The edited leaf and any locked leaves stay fixed; the remaining unlocked
    // leaves absorb the difference so the total never exceeds the budget.
    const adjustable = allLeaves.filter((c) => c.id !== id && !locked[c.id]);
    const adjustableLY = adjustable.reduce((s, c) => s + c.lyRev, 0);

    setPlanValues((prev) => {
      const lockedSum = allLeaves
        .filter((c) => c.id !== id && locked[c.id])
        .reduce((s, c) => s + (prev[c.id] || 0), 0);
      const remaining = Math.max(0, totalBudget - lockedSum - editedValue);
      const next = { ...prev, [id]: editedValue };
      if (adjustable.length > 0) {
        if (adjustableLY > 0) {
          adjustable.forEach((c) => {
            next[c.id] = (c.lyRev / adjustableLY) * remaining;
          });
        } else {
          const perLeaf = remaining / adjustable.length;
          adjustable.forEach((c) => {
            next[c.id] = perLeaf;
          });
        }
      }
      return next;
    });
    triggerAutoSave();
  };

  const toggleLock = (id) => {
    setLocked((prev) => ({ ...prev, [id]: !prev[id] }));
    triggerAutoSave();
  };

  // ── Budget distribution ──────────────────────────────────────────────────
  // Distributes `budget` across all unlocked children proportionally by LY
  // revenue. Locked children keep their current plan values unchanged.
  const distributeBudget = useCallback(
    (budget, overrideLocked) => {
      const lockedState = overrideLocked ?? locked;
      const allChildren = [];
      categories.forEach((parent) => {
        (parent.children || []).forEach((child) => {
          allChildren.push({
            id: child._id.toString(),
            lyRev: child.ly_revenue || 0,
          });
        });
        // Parents without children treated as leaf
        if (!parent.children?.length) {
          allChildren.push({
            id: parent._id.toString(),
            lyRev: parent.ly_revenue || 0,
          });
        }
      });

      const lockedChildren = allChildren.filter((c) => lockedState[c.id]);
      const unlockedChildren = allChildren.filter((c) => !lockedState[c.id]);

      const lockedSum = lockedChildren.reduce(
        (s, c) => s + (planValues[c.id] || 0),
        0,
      );
      const remaining = Math.max(0, budget - lockedSum);
      const unlockedLYTotal = unlockedChildren.reduce((s, c) => s + c.lyRev, 0);

      setPlanValues((prev) => {
        const next = { ...prev };
        if (unlockedLYTotal > 0) {
          unlockedChildren.forEach((c) => {
            next[c.id] = (c.lyRev / unlockedLYTotal) * remaining;
          });
        } else if (unlockedChildren.length > 0) {
          const perChild = remaining / unlockedChildren.length;
          unlockedChildren.forEach((c) => {
            next[c.id] = perChild;
          });
        }
        return next;
      });
    },
    [categories, locked, planValues],
  );

  const handleEditBudget = useCallback(
    (val) => {
      // A user-set budget becomes the new base for the start-of-year scenario.
      baseBudgetRef.current = val;
      setTotalBudget(val);
      setPrintM(budgetToPrintM(val));
      distributeBudget(val);
      triggerAutoSave();
    },
    [distributeBudget, triggerAutoSave],
  );

  // ── Print panel handlers ─────────────────────────────────────────────────
  const handlePrintTotalChange = useCallback(
    (totalCopies) => {
      const totalK = Math.max(0, Math.round(totalCopies / 1000));
      const m = PRINT_SEASONAL.map((s) => Math.round(totalK * s));
      const diff = totalK - m.reduce((s, v) => s + v, 0);
      m[11] += diff;
      setPrintM(m);
      const newBudget = Math.round(totalK * SPC) * 1000;
      baseBudgetRef.current = newBudget;
      setTotalBudget(newBudget);
      distributeBudget(newBudget);
      triggerAutoSave();
    },
    [distributeBudget, triggerAutoSave],
  );

  const handlePrintMonthChange = useCallback(
    (monthIdx, newK) => {
      setPrintM((prev) => {
        const m = [...prev];
        m[monthIdx] = Math.max(0, Math.round(newK));
        const totalK = m.reduce((s, v) => s + v, 0);
        const newBudget = Math.round(totalK * SPC) * 1000;
        baseBudgetRef.current = newBudget;
        setTotalBudget(newBudget);
        distributeBudget(newBudget);
        triggerAutoSave();
        return m;
      });
    },
    [distributeBudget, triggerAutoSave],
  );

  const handlePrintQuarterChange = useCallback(
    (qIdx, newTotalK) => {
      const quarters = [
        [0, 1, 2],
        [3, 4, 5],
        [6, 7, 8],
        [9, 10, 11],
      ];
      const q = quarters[qIdx];
      setPrintM((prev) => {
        const m = [...prev];
        const oldQTotal = q.reduce((s, i) => s + (m[i] || 0), 0);
        if (oldQTotal > 0) {
          q.forEach((i) => {
            m[i] = Math.round(newTotalK * ((m[i] || 0) / oldQTotal));
          });
        } else {
          q.forEach((i) => {
            m[i] = Math.round(newTotalK / 3);
          });
        }
        // Reconcile rounding within quarter to last month
        const qSum = q.reduce((s, i) => s + m[i], 0);
        m[q[2]] += Math.round(newTotalK) - qSum;
        const totalK = m.reduce((s, v) => s + v, 0);
        const newBudget = Math.round(totalK * SPC) * 1000;
        baseBudgetRef.current = newBudget;
        setTotalBudget(newBudget);
        distributeBudget(newBudget);
        triggerAutoSave();
        return m;
      });
    },
    [distributeBudget, triggerAutoSave],
  );

  // Recalculate plan allocations from current print-implied budget
  const handlePrintRecalc = useCallback(() => {
    const totalK = (printM || []).reduce((s, v) => s + v, 0);
    const newBudget = Math.round(totalK * SPC) * 1000;
    baseBudgetRef.current = newBudget;
    setTotalBudget(newBudget);
    distributeBudget(newBudget);
    triggerAutoSave();
  }, [printM, distributeBudget, triggerAutoSave]);

  // ── Monthly plan per leaf (raw $) ──────────────────────────────────────
  // Closed months = actuals; open months split what's left of the row's
  // annual plan by its shape (month edits / saved split), else LY seasonality.
  const planMonthlyById = useMemo(() => {
    const out = {};
    categories.forEach((parent) => {
      const leaves = parent.children?.length ? parent.children : [parent];
      leaves.forEach((leaf) => {
        const id = leaf._id.toString();
        out[id] = normalizeRowMonthly(
          planValues[id] || 0,
          planShapes[id],
          leaf.ytd_monthly,
          closedMonth,
          leaf.trend,
        );
      });
    });
    return out;
  }, [categories, planValues, planShapes, closedMonth]);

  // ── Forecast runs ──────────────────────────────────────────────────────
  const savedPlanId = planId || autoSavedIdRef.current;
  const loadRuns = useCallback(async () => {
    if (!savedPlanId) return;
    try {
      const list = await planForecastRunService.latest(savedPlanId);
      const next = Object.fromEntries(list.map((r) => [r.row_key, r]));
      // Tell the planner when a run they were waiting on finishes.
      Object.values(next).forEach((r) => {
        const before = runsRef.current[r.row_key];
        if (before?.status !== "running" || r.status === "running") return;
        const name = r.row_name || "the category";
        if (r.status === "cancelled") toast.info(`Forecast for ${name} was cancelled.`);
        else if (r.status === "error") toast.error(`Forecast for ${name} failed: ${r.error || "unknown error"}`);
        else if (r.status === "partial")
          toast.warn(`Forecast for ${name} is ready — ${r.failed_count} of ${r.sku_count} SKUs couldn't be forecast.`);
        else toast.success(`Forecast for ${name} is ready.`);
      });
      runsRef.current = next;
      setForecastRuns(next);
    } catch (err) {
      console.warn("Forecast runs load failed:", err);
    }
  }, [savedPlanId]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Poll while any run is in progress.
  const anyRunRunning = Object.values(forecastRuns).some((r) => r.status === "running");
  useEffect(() => {
    if (!anyRunRunning) return undefined;
    const t = setInterval(loadRuns, 10000);
    return () => clearInterval(t);
  }, [anyRunRunning, loadRuns]);

  // Forecast per plan row (leaf) from the newest finished run covering it —
  // a run of the row itself or of its parent category (result.rows).
  const runForecastByLeaf = useMemo(() => {
    const out = {};
    Object.values(forecastRuns)
      // The row's newest finished run: the latest, else the one before a
      // running / cancelled / failed run.
      .map((r) => (r.status === "done" || r.status === "partial" ? r : r.last_finished))
      .filter((r) => r && r.result?.rows)
      .sort((a, b) => new Date(a.started_at) - new Date(b.started_at))
      .forEach((r) => {
        Object.entries(r.result.rows).forEach(([key, v]) => {
          out[key] = { ...v, run_id: r._id, started_at: r.started_at, stale: r.stale };
        });
      });
    return out;
  }, [forecastRuns]);

  // Rows as displayed: a row covered by a finished forecast run projects the
  // year from it — actual so far + the run's forecast for the remaining
  // months — instead of the sales-pace estimate (year_proj from category-sales).
  // Parents are re-summed from their children; pace follows the projection.
  const displayCategories = useMemo(() => {
    const projected = (node) => {
      const run = runForecastByLeaf[node._id.toString()];
      if (!run?.revenue) return node;
      const actual = (node.ytd_monthly || [])
        .slice(0, currentMonth)
        .reduce((s, v) => s + (v || 0), 0);
      const rest = run.revenue
        .slice(currentMonth)
        .reduce((s, v) => s + (v || 0), 0);
      return withProj(node, Math.round(actual + rest));
    };
    const withProj = (node, yearProj) => {
      // No pace before the year starts (pace_pct null), same as the server.
      if (node.pace_pct == null || !(node.ly_revenue > 0)) {
        return { ...node, year_proj: yearProj, year_proj_source: "run" };
      }
      const pct = Math.round(((yearProj - node.ly_revenue) / node.ly_revenue) * 1000) / 10;
      return {
        ...node,
        year_proj: yearProj,
        year_proj_source: "run",
        pace_pct: pct,
        pace_status: pct > 0 ? "AHEAD" : pct >= -5 ? "ON TRACK" : "BEHIND",
      };
    };
    return categories.map((parent) => {
      if (!parent.children?.length) return projected(parent);
      const children = parent.children.map(projected);
      const changed = children.some((c, i) => c !== parent.children[i]);
      if (!changed) return parent;
      const delta = children.reduce(
        (s, c, i) => s + (c.year_proj || 0) - (parent.children[i].year_proj || 0),
        0,
      );
      return withProj({ ...parent, children }, Math.round((parent.year_proj || 0) + delta));
    });
  }, [categories, runForecastByLeaf, currentMonth]);

  const filteredCategories =
    activeFilter === "ALL"
      ? displayCategories
      : displayCategories.filter((c) => c.name.toUpperCase() === activeFilter);
  const totalYearProj = displayCategories.reduce((s, p) => s + (p.year_proj || 0), 0);
  const yearProjVsPlanPct =
    allocated > 0 ? Math.round((totalYearProj / allocated) * 1000) / 10 : null;

  // ── Channel filter ───────────────────────────────────────────────────────
  // The server works out the plan per channel on GET (each channel's sales
  // ratio of every category-month). Re-read it when a channel is picked and
  // after a save while one is viewed.
  // One request at a time: a call while one is in flight shares it instead
  // of starting another, so slow responses never pile up.
  const channelRequestRef = useRef(null);
  const refreshChannelView = useCallback(() => {
    if (!savedPlanId) return Promise.resolve();
    if (channelRequestRef.current) return channelRequestRef.current;
    channelRequestRef.current = catalogPlanService
      .getById(savedPlanId)
      .then((plan) => {
        setChannelView(plan.channel_view || null);
        setChannelStatus(channelStatusOf(plan));
      })
      .catch((err) => console.warn("Channel view refresh failed:", err))
      .finally(() => {
        channelRequestRef.current = null;
      });
    return channelRequestRef.current;
  }, [savedPlanId]);

  useEffect(() => {
    if (activeChannel !== "ALL") refreshChannelView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel]);
  useEffect(() => {
    if (autoSaveStatus !== "saved") return undefined;
    refreshChannelView();
    // The plan's per-channel sales are worked out in the background after a
    // first save — read again once they're likely in. Not cleared on the
    // status change ("saved" turns back to "idle" after 2.5 s); only when
    // the page closes.
    clearTimeout(channelRefreshTimer.current);
    channelRefreshTimer.current = setTimeout(refreshChannelView, 8000);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveStatus]);
  useEffect(() => () => clearTimeout(channelRefreshTimer.current), []);

  // While the server works out the channel allocation (a new plan after its
  // first save, or an older plan on open), check again until it's in — the
  // next check is scheduled 5 s after the previous one finishes, never on a
  // fixed clock, and checking stops after 10 minutes.
  useEffect(() => {
    if (channelStatus !== "preparing" || !savedPlanId) return undefined;
    let cancelled = false;
    let timer = null;
    const started = Date.now();
    const next = () => {
      if (cancelled || Date.now() - started > 10 * 60 * 1000) return;
      timer = setTimeout(() => refreshChannelView().then(next), 5000);
    };
    next();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [channelStatus, savedPlanId, refreshChannelView]);

  const activeChannelView =
    activeChannel !== "ALL" ? (channelView || []).find((v) => v.channel === activeChannel) || null : null;

  // The plan as one channel sees it: the same rows with that channel's last
  // year, actuals and plan. Read-only; edits happen in All channels.
  const channelDisplay = useMemo(() => {
    if (!activeChannelView) return null;
    const zero = () => Array(12).fill(0);
    const sumArr = (a) => (a || []).reduce((s, v) => s + (Number(v) || 0), 0);
    const addArr = (list) => list.reduce((acc, a) => acc.map((v, i) => v + (Number(a?.[i]) || 0)), zero());
    const planValuesCh = {};
    const planMonthlyCh = {};
    const leafOf = (node) => {
      const id = node._id.toString();
      const r = activeChannelView.rows?.[id] || {};
      const planM = r.plan_monthly || zero();
      const actual = (r.actual_monthly || zero()).map((v) => Number(v) || 0);
      planValuesCh[id] = sumArr(planM);
      planMonthlyCh[id] = planM;
      return {
        ...node,
        ly_revenue: sumArr(r.ly_monthly),
        trend: r.ly_monthly || zero(),
        ytd_monthly: actual,
        ytd_revenue: sumArr(actual),
        ytd_monthly_units: (r.actual_units_monthly || zero()).map((v) => Number(v) || 0),
        ly_forecast_from_month: null,
        year_proj: sumArr(planM), // actuals so far + the channel's plan for the rest
        pace_pct: null,
        pace_status: "N/A",
        forecast_monthly: null,
      };
    };
    const cats = displayCategories.map((parent) => {
      if (!parent.children?.length) return leafOf(parent);
      const children = parent.children.map(leafOf);
      return {
        ...parent,
        children,
        ly_revenue: children.reduce((s, c) => s + c.ly_revenue, 0),
        trend: addArr(children.map((c) => c.trend)),
        ytd_monthly: addArr(children.map((c) => c.ytd_monthly)),
        ytd_revenue: children.reduce((s, c) => s + c.ytd_revenue, 0),
        ytd_monthly_units: addArr(children.map((c) => c.ytd_monthly_units)),
        ly_forecast_from_month: null,
        year_proj: children.reduce((s, c) => s + c.year_proj, 0),
        pace_pct: null,
        pace_status: "N/A",
        forecast_monthly: null,
      };
    });
    const filtered =
      activeFilter === "ALL" ? cats : cats.filter((c) => c.name.toUpperCase() === activeFilter);
    const budget = activeChannelView.plan || 0;
    const ly = activeChannelView.ly || 0;
    return {
      categories: cats,
      filtered,
      planValues: planValuesCh,
      planMonthlyById: planMonthlyCh,
      budget,
      ly,
      ytd: activeChannelView.actual_ytd || 0,
      yearProj: cats.reduce((s, c) => s + (c.year_proj || 0), 0),
    };
  }, [activeChannelView, displayCategories, activeFilter]);

  const openRunDialog = (node) => {
    if (!savedPlanId) {
      toast.info("Save the plan first, then run the forecast.");
      return;
    }
    setRunDialog({ rowKey: node._id.toString(), name: node.name });
  };

  const handleRunForecast = async (params) => {
    try {
      const run = await planForecastRunService.start(savedPlanId, runDialog.rowKey, params);
      setRunDialog(null);
      if (run.already_running) {
        toast.info(`A forecast for ${run.row_name || runDialog.name} is already running.`);
      } else {
        toast.info(
          `Forecasting ${run.sku_count} SKUs in ${run.row_name || runDialog.name} — ${fmtDuration(run.est_seconds)}. You'll get a notice here when it's ready.`,
        );
      }
      const next = { ...runsRef.current, [run.row_key]: { ...run, done_count: 0 } };
      runsRef.current = next;
      setForecastRuns(next);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "The forecast could not be started.");
    }
  };

  // Cancel asks first (CancelForecastDialog), then stops the run.
  const handleCancelForecast = (run) => setCancelTarget(run);

  const confirmCancelForecast = async () => {
    const run = cancelTarget;
    if (!run) return;
    const name = run.row_name || "this category";
    setCancelling(true);
    try {
      const res = await planForecastRunService.cancel(run._id);
      const next = { ...runsRef.current, [run.row_key]: { ...run, ...res } };
      runsRef.current = next;
      setForecastRuns(next);
      toast.info(`Forecast for ${name} was cancelled.`);
      loadRuns();
    } catch (err) {
      toast.error(err?.response?.data?.message || "The forecast could not be cancelled.");
    } finally {
      setCancelling(false);
      setCancelTarget(null);
    }
  };

  // The category name rides along for the breadcrumb ("Forecasted · Sandal").
  // ⋮ › Email Excel: the latest finished run's workbook, to the signed-in user.
  const handleEmailForecast = async (run) => {
    const name = run?.row_name || "this category";
    const toastId = toast.info(`Emailing the ${name} forecast…`, { autoClose: false });
    try {
      const res = await planForecastRunService.email(run._id);
      toast.update(toastId, {
        render: `The ${name} forecast Excel was sent to ${res?.email?.to || "your email"}.`,
        type: "success",
        autoClose: 5000,
      });
    } catch (err) {
      toast.update(toastId, {
        render: err?.response?.data?.message || "The email could not be sent.",
        type: "error",
        autoClose: 6000,
      });
    }
  };

  const handleViewForecast = (run, node) => {
    if (!run?._id || !savedPlanId) return;
    navigate(`/catalog-planner/${savedPlanId}/forecast/${run._id}`, {
      state: { categoryName: node?.name || run.row_name || null },
    });
  };

  // A month edit in the monthly grid: `monthly` is the row's 12 raw $ (closed
  // months unchanged). It becomes the row's shape; its total is the row's new
  // annual plan, which rebalances the other unlocked rows as usual.
  const handlePlanMonthlyChange = (id, monthly) => {
    setPlanShapes((prev) => ({ ...prev, [id]: monthly }));
    const total = monthly.reduce((s, v) => s + (Number(v) || 0), 0);
    handlePlanChange(id, String(total / 1000));
  };

  // Each allocation is keyed by the plan's dimension.
  const allocationRow = (node) => {
    const id = node._id.toString();
    const catStatus = categoryStatuses[id];
    const persistStatus =
      catStatus === "review" || catStatus === "approved" ? catStatus : "draft";
    return {
      [isProductType ? "product_type" : "category_id"]: id,
      plan_value: planValues[id] || 0,
      plan_monthly: planMonthlyById[id],
      locked: !!locked[id],
      status: persistStatus,
      forecast_monthly: node.forecast_monthly || [],
      ytd_monthly_units: node.ytd_monthly_units || [],
    };
  };

  const buildCategoriesPayload = () =>
    categories.flatMap((parent) =>
      parent.children?.length
        ? parent.children.map(allocationRow)
        : [allocationRow(parent)],
    );

  // Update always-current payload snapshot each render (read inside auto-save timer)
  buildPayloadRef.current = () => ({
    client_id: clientId,
    name: modalName.trim(),
    year: modalYear,
    status: modalStatus,
    scenario,
    dimension,
    total_budget: totalBudget,
    categories: buildCategoriesPayload(),
  });
  // Keep ref current so polling closure can call the latest triggerAutoSave
  triggerAutoSaveRef.current = triggerAutoSave;

  const handleSave = async () => {
    if (!modalName.trim()) return;
    // Cancel any pending auto-save — manual save takes priority
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        name: modalName.trim(),
        year: modalYear,
        status: modalStatus,
        scenario,
        dimension,
        total_budget: totalBudget,
        categories: buildCategoriesPayload(),
      };
      let saved;
      if (planId) {
        saved = await catalogPlanService.update(planId, payload);
        setPlanMeta(saved);
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus("idle"), 2500);
      } else {
        saved = await catalogPlanService.create(payload);
        navigate("/catalog-plans");
      }
    } catch (err) {
      console.error("Save plan error:", err);
    } finally {
      setSaving(false);
    }
  };

  // ── Category status cycle (draft → review → approved → draft) ──────────────
  const handleCategoryStatusToggle = async (categoryId, currentStatus) => {
    if (!planId) return;
    const CYCLE = ["draft", "review", "approved"];
    // Map legacy 'active' → 'approved' before cycling
    const normalised =
      currentStatus === "active"
        ? "approved"
        : CYCLE.includes(currentStatus)
          ? currentStatus
          : "draft";
    const newStatus = CYCLE[(CYCLE.indexOf(normalised) + 1) % CYCLE.length];
    setCategoryStatuses((prev) => ({ ...prev, [categoryId]: newStatus }));
    try {
      await catalogPlanManagementService.updateCategoryStatus(
        planId,
        categoryId,
        newStatus,
      );
    } catch (err) {
      console.error("Category status update error:", err);
      // revert on failure
      setCategoryStatuses((prev) => ({
        ...prev,
        [categoryId]: currentStatus,
      }));
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        p: 2.5,
        bgcolor: darkMode ? "#121212" : "#f4f6f8",
        minHeight: "100vh",
      }}
    >
      {/* ── Top bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Back to plans">
            <IconButton
              size="small"
              onClick={() => navigate("/catalog-plans")}
              sx={{ color: textSecondary }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            value={modalName}
            onChange={(e) => {
              setModalName(e.target.value);
              triggerAutoSave();
            }}
            placeholder="Plan name"
            variant="standard"
            inputProps={{
              style: { fontSize: 14, color: textPrimary },
            }}
            sx={{ minWidth: 200 }}
          />
          <Select
            value={modalStatus}
            onChange={(e) => {
              setModalStatus(e.target.value);
              triggerAutoSave();
            }}
            variant="standard"
            sx={{
              fontSize: 14,
              color: modalStatus === "active" ? "#2e7d32" : textSecondary,
              minWidth: 80,
              "& .MuiSelect-icon": { fontSize: 16 },
            }}
          >
            <MenuItem value="draft">draft</MenuItem>
            <MenuItem value="active">active</MenuItem>
            <MenuItem value="archived">archived</MenuItem>
          </Select>
          <Select
            value={scenario}
            onChange={(e) => {
              setScenario(e.target.value);
              triggerAutoSave();
            }}
            variant="standard"
            sx={{
              fontSize: 14,
              color: textSecondary,
              minWidth: 110,
              "& .MuiSelect-icon": { fontSize: 16 },
            }}
          >
            <MenuItem value="start-of-year">Start of Year</MenuItem>
            <MenuItem value="mid-year">Mid Year</MenuItem>
            <MenuItem value="end-of-year">End of Year</MenuItem>
            <MenuItem value="match-ly">Match Last Year</MenuItem>
          </Select>
          {/* Dimension shown only when it deviates from the default (category). */}
          {isProductType && (
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: darkMode ? "#94a3b8" : "#475569",
                bgcolor: darkMode ? "#2a2a2a" : "#F1F3F5",
                borderRadius: "10px",
                px: 1,
                py: 0.4,
              }}
            >
              Product Type
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Auto-save status indicator */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {autoSaveStatus === "saving" ? (
              <>
                <CircularProgress size={13} sx={{ color: "#1976d2" }} />
                <Typography
                  variant="caption"
                  sx={{ color: textSecondary, fontStyle: "italic" }}
                >
                  saving…
                </Typography>
              </>
            ) : autoSaveStatus === "saved" ? (
              <>
                <CloudDoneOutlinedIcon
                  sx={{ fontSize: 16, color: "#2e7d32" }}
                />
                <Typography variant="caption" sx={{ color: "#2e7d32" }}>
                  saved
                </Typography>
              </>
            ) : (
              <Tooltip title="Auto-save active">
                <CloudOutlinedIcon
                  sx={{ fontSize: 16, color: textSecondary, opacity: 0.45 }}
                />
              </Tooltip>
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveOutlinedIcon />}
            onClick={handleSave}
            disabled={saving || autoSaveStatus === "saving"}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {saving ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : planId ? (
              "Update Plan"
            ) : (
              "Save Plan"
            )}
          </Button>
        </Box>
      </Box>

      {/* <PrintPanel
        printM={printM}
        currentMonth={currentMonth}
        currentYearData={currentYearData}
        onTotalChange={handlePrintTotalChange}
        onMonthChange={handlePrintMonthChange}
        onQuarterChange={handlePrintQuarterChange}
        onRecalc={handlePrintRecalc}
        darkMode={darkMode}
      /> */}

      {loading ? (
        <PlannerSkeleton darkMode={darkMode} />
      ) : (
      <>
      {channelDisplay && (
        <Typography sx={{ fontSize: 12.5, color: textSecondary, mb: 1.5 }}>
          Viewing <b>{activeChannel}</b>: the plan by this channel's share of each category's sales, month by month.
          Closed months show its actual sales. Switch to All channels to edit the plan.
        </Typography>
      )}
      <BudgetSummary
        lyForecastRange={channelDisplay ? "" : lyForecastRangeOf(categories)}
        totalBudget={channelDisplay ? channelDisplay.budget : totalBudget}
        onEditBudget={handleEditBudget}
        readOnly={!!channelDisplay}
        allocated={channelDisplay ? channelDisplay.budget : allocated}
        variance={channelDisplay ? 0 : variance}
        planVsLYPct={
          channelDisplay
            ? channelDisplay.ly > 0
              ? Math.round(((channelDisplay.budget - channelDisplay.ly) / channelDisplay.ly) * 1000) / 10
              : null
            : planVsLYPct
        }
        planVsDelta={channelDisplay ? channelDisplay.budget - channelDisplay.ly : planVsDelta}
        totalYTDActual={channelDisplay ? channelDisplay.ytd : totalYTDActual}
        ytdPlanApprox={channelDisplay ? channelDisplay.ytd : ytdPlanApprox}
        totalYearProj={channelDisplay ? channelDisplay.yearProj : totalYearProj}
        currentYearData={currentYearData}
        totalSubcategories={totalSubcategories}
        allLockedCount={allLockedCount}
        lockedAllocated={lockedAllocated}
        darkMode={darkMode}
      />

      <PlannerTable
        categories={channelDisplay ? channelDisplay.categories : displayCategories}
        filteredCategories={channelDisplay ? channelDisplay.filtered : filteredCategories}
        readOnly={!!channelDisplay}
        channelView={channelView || []}
        channelStatus={channelStatus}
        activeChannel={activeChannel}
        onChannelSelect={setActiveChannel}
        filterTabs={filterTabs}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        planValues={channelDisplay ? channelDisplay.planValues : planValues}
        locked={locked}
        expanded={expanded}
        scenario={scenario}
        setScenario={setScenario}
        toggleExpand={toggleExpand}
        expandAll={expandAll}
        collapseAll={collapseAll}
        handlePlanChange={handlePlanChange}
        toggleLock={toggleLock}
        currentYearData={currentYearData}
        currentMonth={currentMonth}
        totalLY={totalLY}
        allocated={channelDisplay ? channelDisplay.budget : allocated}
        totalYearProj={channelDisplay ? channelDisplay.yearProj : totalYearProj}
        yearProjVsPlanPct={
          channelDisplay
            ? channelDisplay.budget > 0
              ? Math.round((channelDisplay.yearProj / channelDisplay.budget) * 1000) / 10
              : null
            : yearProjVsPlanPct
        }
        darkMode={darkMode}
        loading={loading}
        categoryStatuses={categoryStatuses}
        onCategoryStatusToggle={handleCategoryStatusToggle}
        planId={planId}
        clientId={clientId}
        forecastErrors={forecastErrors}
        forecastPending={forecastPending}
        planMonthlyById={channelDisplay ? channelDisplay.planMonthlyById : planMonthlyById}
        closedMonth={closedMonth}
        savedRowMeta={savedRowMeta}
        onPlanMonthlyChange={handlePlanMonthlyChange}
        forecastRuns={forecastRuns}
        runForecastByLeaf={channelDisplay ? {} : runForecastByLeaf}
        onRunForecast={openRunDialog}
        onViewForecast={handleViewForecast}
        onCancelForecast={handleCancelForecast}
        onEmailForecast={handleEmailForecast}
      />
      </>
      )}
      <RunForecastDialog
        open={!!runDialog}
        onClose={() => setRunDialog(null)}
        onSubmit={handleRunForecast}
        categoryName={runDialog?.name || ""}
        clientId={clientId}
        planYear={modalYear}
      />
      <Dialog
        open={!!cancelTarget}
        onClose={cancelling ? undefined : () => setCancelTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
          Cancel forecast · {cancelTarget?.row_name || "category"}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5 }}>
            {cancelTarget?.done_count ?? 0} of {cancelTarget?.sku_count ?? "?"} SKUs are done. SKUs already being
            forecast will finish; no more will start, and this run won't be shown. The last finished forecast for
            this category stays as it is.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling}>
            Keep running
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmCancelForecast}
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Cancel forecast
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
